<template>
  <Combobox as="div" @update:modelValue="handleChange" v-slot="{ open }">
    <div>
      <ComboboxButton
        class="w-full cursor-default rounded-md bg-neutral-white text-left text-gray-900 sm:text-sm sm:leading-6 flex items-center justify-between"
        :class="{
          'opacity-50': disabled,
        }"
        :disabled="disabled"
        ref="trigger"
        v-bind="$attrs"
        v-hubble="'trigger'"
      >
        <PulseButton :variant="variant" :size="size" :disabled="disabled">
          <template #icon-left>
            <PulseLinePlus class="h-3 w-3 text-neutral-500 fill-current" />
          </template>
          {{ title }}
        </PulseButton>
      </ComboboxButton>

      <transition
        leave-active-class="transition ease-in duration-100"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
        @enter="handleEnter"
      >
        <div
          class="border rounded-md border-neutral-200 flex flex-col bg-neutral-white shadow-md z-10"
          ref="menu"
          v-if="open"
        >
          <div class="flex-none flex items-center gap-2" v-if="searchable">
            <ComboboxInput
              placeholder="Search..."
              class="px-5 py-3 border-b border-neutral-200 bg-neutral-white w-full outline-none"
              @change="search = $event.target.value"
              ref="input"
              :on-vnode-before-unmount="() => (search = '')"
            />
            <XMarkIcon
              class="text-typography-secondary h-6 w-6 cursor-pointer"
              @click="search = ''"
              v-if="search != ''"
            />
          </div>
          <div class="flex-1 overflow-auto">
            <ComboboxOptions
              v-hubble="'menu'"
              class="max-h-60 rounded-md bg-neutral-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm overflow-auto"
            >
              <ComboboxOption
                as="template"
                v-for="item in visibleItems"
                :key="item.value"
                :value="item"
                v-slot="{ active }"
              >
                <li
                  :class="{
                    'bg-neutral-200': active,
                    'text-typography-primary cursor-default select-none py-2 px-3 flex items-center justify-between': true,
                    'bg-primary-100': isSelected(item)
                  }"
                  :aria-selected="isSelected(item)"
                >
                  <span :class="[isSelected(item) ? 'font-semibold' : 'font-normal', 'block truncate']">
                    {{ item.label }}
                  </span>
                  <CheckIcon v-if="isSelected(item)" class="h-5 w-5 text-primary-700" aria-hidden="true" />
                </li>
              </ComboboxOption>
            </ComboboxOptions>
          </div>
        </div>
      </transition>
    </div>
  </Combobox>
</template>

<script setup lang="ts">
import { createPopper } from '@popperjs/core';
import {
  Combobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
} from '@headlessui/vue';
import { CheckIcon, XMarkIcon } from '@heroicons/vue/20/solid';
import { type PropType, ref, computed, watch, inject } from 'vue';

interface iDropdownItem {
  value: any;
  label: string;
}

const trigger = ref<any>(null);
const menu = ref<any>(null);
const search = ref('');
const input = ref<any>(null);

const props = defineProps({
  error: Boolean,
  items: {
    type: Array as PropType<iDropdownItem[]>,
    required: true,
  },
  name: String,
  title: {
    type: String,
    default: '',
  },
  icon: {
    type: String,
    default: '',
  },
  modelValue: {
    type: [String, Number, Boolean, Object, Array] as PropType<any>,
    default: null,
  },
  searchable: {
    type: Boolean,
    default: true,
  },
  variant: {
    type: String,
    default: 'secondary',
  },
  popperPlacement: {
    type: String,
    default: 'bottom-start',
  },
  dropdownWidth: String,
  selectedOptions: {
    type: Array as PropType<iDropdownItem[]>,
    default: null,
  },
  size: {
    type: String,
    default: 'small',
  },
});

const addFilterDisabled = inject('addFilterDisabled', ref(false));
const disabled = computed(() => {
  return addFilterDisabled.value;
});

const emit = defineEmits(['update:modelValue']);

const visibleItems = computed(() => {
  if (!search.value) return props.items;
  return props.items.filter(item =>
    item.label.toLowerCase().includes(search.value.toLowerCase())
  );
});

function isSelected(item: iDropdownItem) {
  return props.selectedOptions?.some(selectedItem => selectedItem.value === item.value) || false;
}

function handleChange(item: iDropdownItem) {
  emit('update:modelValue', item.value);
}

watch(
  () => input.value,
  () => {
    if (!input.value) return;
    search.value = '';
    input.value.el.focus();
  },
);

watch(
  () => visibleItems.value,
  () => {
    scrollSelectedOptionIntoView();
  },
);

function initPopper() {
  const popper = createPopper(trigger.value.el, menu.value, {
    placement: props.popperPlacement || 'bottom-start',
    modifiers: [
      {
        name: 'offset',
        options: {
          offset: [0, 10],
        },
      },
      {
        effect: ({ state }) => {
          state.elements.popper.style.width = `${state.elements.reference.offsetWidth}px`;
        },
        enabled: true,
        fn: ({ state }) => {
          state.styles.popper.width = props.dropdownWidth || `${state.rects.reference.width}px`;
        },
        name: 'sameWidth',
        phase: 'beforeWrite',
        requires: ['computeStyles'],
      },
      {
        name: 'flip',
        options: {
          fallbackPlacements: ['top', 'bottom'],
        },
      },
    ],
    strategy: 'fixed',
  });

  popper.forceUpdate();
}

function handleEnter() {
  initPopper();
  scrollSelectedOptionIntoView();
}

function scrollSelectedOptionIntoView() {
  const selectedEl = menu.value?.querySelector('[aria-selected="true"]');
  if (selectedEl) {
    selectedEl.scrollIntoView();
  }
}
</script>
